import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import ZCodeMirrorReactWrapper, {
	CodeMirror
} from "./ZCodeMirrorReactWrapperWithCustomMode";
import { IEditorInstance } from "./ZCodeMirrorReactWrapperWithCustomMode/interface";
import {
	ZButton,
	ZCollapse,
	ZIcon,
	ZPopover,
	ZTypography
} from "@atoms";
import { ZMenu, ZMergeTagCascadeDropdown } from "@molecules";
import ReactDOM from "react-dom";
import { EIcons } from "@atoms/ZIcon/interface";
import ZFormulaTest from "./ZFormulaTest";
import { IZFormulaEditorProps } from "./interface";
import "./index.less";
import { MergeTagVariableDataType } from "@/Contracts/Documents";
import ZFormulaIfDocumentation from "./ZFormulaFunctionDocumentation/ZFormulaIfDocumention";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { generateFormula as generateFormulaService } from "./services";
import { useRouteMatch } from "react-router-dom";
import { ZomentumEntities, ZomentumRoutes } from "@/Contracts";
import { useFeatureFlag } from "@/Hooks";
import ZTryWithAiButton from "./ZTryWithAiButton";
import { EColors } from "@atoms/ZColors/interface";

const ZFormulaEditor: React.FC<IZFormulaEditorProps> = ({
	fieldName,
	defaultValue = "1 + 1",
	mergeTags,
	onChange,
	updateTestFormulaValidState
}) => {
	const formulaFieldsGenerateFormulaFlag = useFeatureFlag(
		"formula_fields_generate_formula"
	);
	const route = useRouteMatch<{ entity: ZomentumEntities }>(
		ZomentumRoutes.CustomFieldsList
	);
	const [testedFormula, setTestedFormula] = useState<string>("");
	useEffect(() => {
		onChange(testedFormula);
	}, [testedFormula]);

	const filteredMergeTags = useMemo(
		() =>
			mergeTags.filter(
				mergeTag => mergeTag.data_type !== MergeTagVariableDataType.DATE
			),
		[mergeTags]
	);
	const zFormulaEditorRef = useRef<HTMLDivElement>(null);

	const editorRef = useRef<IEditorInstance>(null);
	const myEditor = editorRef.current?.editor;

	const [isEditorInitialized, setIsEditorInitialized] = useState(false);

	// to check if editor is initialized
	useEffect(() => {
		const onEditorChange = (
			_editor: CodeMirror.Editor,
			change: CodeMirror.EditorChange
		) => {
			updateTestFormulaValidState(false);
			handleMergeTagsUsed(change);
			setUnableToGenerateFormula(false);
		};
		// Only set up the interval if the ref is not yet initialized
		// Function to check if the ref is initialized and update state
		const checkIfRefIsInitialized = () => {
			if (editorRef.current?.editor !== null) {
				setIsEditorInitialized(true); // Update state to reflect initialization
				// Since ref is initialized, clear the interval
				clearInterval(intervalId);
				// Attach the change event listener to the editor
				editorRef.current?.editor.on("change", onEditorChange);
			}
		};

		// Set up an interval to check the ref every 500 milliseconds
		const intervalId = setInterval(checkIfRefIsInitialized, 500);

		// Cleanup function to clear the interval
		return () => {
			clearInterval(intervalId);
			// Remove the change event listener when the component is unmounted
			editorRef.current?.editor.off("change", onEditorChange);
		};
	}, []); // Depend on isInitialized to re-run effect if needed

	const updateEditorValue = useCallback(
		(newValue: string) => {
			if (myEditor) {
				const uniqueMergeTags = new Set(newValue.match(/\{\{.*?\}\}/g));
				const uniqueMergeTagsArray = Array.from(uniqueMergeTags);
				uniqueMergeTagsArray.forEach(uniqueMergeTag => {
					const mergeTag = mergeTags.find(
						mergeTag =>
							`{{${mergeTag.variable_name}}}` === uniqueMergeTag
					);
					if (mergeTag) {
						const regex = new RegExp(uniqueMergeTag, "g");
						newValue = newValue.replace(
							regex,
							`{{${mergeTag.display_name}}}`
						);
					}
				});
				myEditor.setValue(newValue); // set default value
				let lineNo = 0;
				myEditor.eachLine(line => {
					const regex = /\{\{.*?\}\}/g;
					let match;
					while ((match = regex.exec(line.text)) !== null) {
						const placeholder = match[0];
						const mergeTag = mergeTags.find(
							tag => `{{${tag.display_name}}}` === placeholder
						);
						if (!mergeTag) {
							continue;
						}
						const from = { line: lineNo, ch: match.index };
						const to = {
							line: lineNo,
							ch: match.index + placeholder.length
						};

						const widgetElement = document.createElement("span");
						widgetElement.className = "variable";
						widgetElement.innerHTML = placeholder;
						widgetElement.style.background = "yellow"; // Example styling
						widgetElement.setAttribute(
							"data-id",
							mergeTag.variable_name
						); // Store the placeholder in the widget

						const marker = myEditor.markText(from, to, {
							replacedWith: widgetElement
						});
						const markerFind = marker?.find();
						if (markerFind) {
							regex.lastIndex = markerFind.to.ch;
						} else {
							break;
						}
						regex.lastIndex = match.index + placeholder.length; // Update the lastIndex to move past the current match
					}
					lineNo += 1;
				});
				// get all merge tags used in default value
				const event = new Event("onMergeTagsUsed");
				document.dispatchEvent(event);
			}
		},
		[isEditorInitialized, mergeTags]
	);

	useEffect(() => {
		updateEditorValue(defaultValue);
	}, [isEditorInitialized, mergeTags]);

	// fire event when merge tags are used
	const handleMergeTagsUsed = useCallback(
		(change: CodeMirror.EditorChange) => {
			const mergeTagRegex = /\{\{.*?\}\}/;
			if (
				mergeTagRegex.test(change.text[0]) ||
				(change.removed && mergeTagRegex.test(change.removed[0]))
			) {
				const event = new Event("onMergeTagsUsed");
				document.dispatchEvent(event);
			}
		},
		[]
	);

	// handle outside click to close dropdowns
	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				zFormulaEditorRef.current &&
				!zFormulaEditorRef.current.contains(e.target as Node)
			) {
				document.getElementById("autocomplete-dropdown")?.remove();
				document.getElementById("function-description")?.remove();
				setActiveFunctionName(null);
			}
		};
		document.body.addEventListener("click", handleOutsideClick);
		return () => {
			document.body.removeEventListener("click", handleOutsideClick);
		};
	}, []);

	// Define the custom mode with variable highlighting
	useEffect(() => {
		if (myEditor) {
			// Automatic hinting on input
			myEditor.on("change", function () {
				document.getElementById("autocomplete-dropdown")?.remove();
			});
			myEditor.on(
				"inputRead",
				function (
					editor: CodeMirror.Editor,
					input: CodeMirror.EditorChange
				) {
					if (
						input.text[0][0] !== ";" &&
						input.text[0][0] !== " " &&
						input.text[0][0] !== "("
					) {
						showCustomAutocomplete(editor, {
							text: input.text[0], // Access the first element of the input.text array
							from: input.from,
							to: input.to
						});
					}
				}
			);
		}
	}, [isEditorInitialized, filteredMergeTags]);

	// Custom autocomplete function
	const showCustomAutocomplete = useCallback(
		(
			editor: CodeMirror.Editor,
			input: {
				text: string;
				from: CodeMirror.Position;
				to: CodeMirror.Position;
			}
		) => {
			// Assuming 'editor' is your CodeMirror instance
			const startPos = { line: input.to.line, ch: 0 }; // Start from the beginning of the document
			const endPos = { line: input.to.line, ch: input.to.ch + 1 }; // End at the start of the third line
			const text = editor.getRange(startPos, endPos);

			const cursor = editor.getCursor(); // Get the current cursor position
			const token = editor.getTokenAt(cursor); // Get the token at the cursor
			const state = token.state; // Get the state from the token

			if (state.inString) {
				return;
			} else if (text.endsWith("{{")) {
				// Your custom component
				const MergeTagDropdown = (
					<ZMergeTagCascadeDropdown
						shouldAllowAddVariable={false}
						visible={true}
						onSubmit={function (key: string) {
							const cursor = editor.getCursor(); // Gets the line number in the cursor position
							const endPos = {
								// create a new object to avoid mutation of the original selection
								line: cursor.line,
								ch: cursor.ch // set the character position to the end of the line
							};
							const startPos = {
								line: cursor.line,
								ch: cursor.ch - 2 // set the character position to the end of the line
							};
							// Replace the range with an empty string to delete the characters
							editor.replaceRange("", startPos, endPos);
							insertMergeTag(editor, startPos, key);
						}}
						onClose={function () {
							document
								.getElementById("autocomplete-dropdown")
								?.remove();
						}}
						mergeTagCustomVariables={[]}
						mergeTagSystemVariables={filteredMergeTags}
					/>
				);
				const dropdown = document.createElement("div");
				dropdown.setAttribute("id", "autocomplete-dropdown");
				zFormulaEditorRef.current?.appendChild(dropdown);
				ReactDOM.render(MergeTagDropdown, dropdown);

				const cursorCoords = editor.cursorCoords(true, "local");

				const editorWidth = zFormulaEditorRef.current?.clientWidth ?? 0;
				const MERGE_TAG_CASCADER_WIDTH = 195;
				const rightX =
					cursorCoords.left + 16 + MERGE_TAG_CASCADER_WIDTH;

				// Position the dropdown based on cursor coordinates
				dropdown.style.left = `${
					rightX > editorWidth
						? editorWidth - MERGE_TAG_CASCADER_WIDTH
						: cursorCoords.left + 16
				}px`;
				dropdown.style.top = `${cursorCoords.bottom + 18}px`; // `bottom` because you want it just below the cursor
				dropdown.style.display = "block";
				dropdown.style.position = "absolute";
			} else {
				CodeMirror.commands.autocomplete(editor, undefined, {
					completeSingle: false
				});
			}
		},
		[filteredMergeTags]
	);

	// Define the autocomplete function with function hints
	const zFormulaEditorCustomHints = useCallback(
		(editor: CodeMirror.Editor, _options: any) => {
			const cursor = editor.getCursor();
			const token = editor.getTokenAt(cursor);
			const start = token.start;
			const end = cursor.ch;
			const currentWord = token.string;
			let list: { text: string; render?: any }[] = [
				{
					text: "if(condition, arg1, arg2)",
					render: (element: HTMLElement) => {
						const content = (
							<div className="">
								<ZTypography.ZText
									weight={EFontWeights.SEMIBOLD}
									className="block"
								>
									if(condition, arg1, arg2)
								</ZTypography.ZText>
								<ZTypography.ZText level={0} className="block">
									Set up a conditional expression
								</ZTypography.ZText>
							</div>
						);
						ReactDOM.render(content, element);
					}
				}
			];
			if (!currentWord) {
				list = [];
			}
			return {
				list: list.filter(function (item) {
					return item.text.startsWith(currentWord);
				}),
				from: CodeMirror.Pos(cursor.line, start),
				to: CodeMirror.Pos(cursor.line, end)
			};
		},
		[]
	);

	// insert merge tag
	const insertMergeTag = useCallback(
		(
			editor: CodeMirror.Editor,
			position: CodeMirror.Position,
			key: string
		) => {
			const mergeTag = filteredMergeTags.find(
				tag => tag.variable_name === key
			);
			if (!mergeTag) {
				return;
			}
			const placeholder = `{{${mergeTag.display_name}}}`; // Unique placeholder text
			editor.replaceRange(placeholder, position); // Insert placeholder at the specified position

			// Find the placeholder and replace it with a widget (HTML element)
			const line = position.line;
			const ch = position.ch + placeholder.length;
			const from = { line, ch: ch - placeholder.length };
			const to = { line, ch: ch };

			const widgetElement = document.createElement("span");
			widgetElement.className = "variable";
			widgetElement.innerHTML = placeholder;
			widgetElement.style.background = "yellow"; // Example styling
			widgetElement.setAttribute("data-id", key); // Store the placeholder in the widget

			editor.markText(from, to, { replacedWith: widgetElement });

			editor.setCursor(to);
			setEditorInsertMergeTagsPopover(false);
			setEditorInsertPopover(false);
			editor.focus();
		},
		[filteredMergeTags]
	);

	// function name on which cursor is so as to show function description
	const [activeFunctionName, setActiveFunctionName] = useState<string | null>(
		null
	);

	// show function documentation when cursor is on function
	const showFunctionDocumentation = useCallback(
		(editor: CodeMirror.Editor) => {
			const cursor = editor.getCursor(); // Get the current cursor position
			const token = editor.getTokenAt(cursor); // Get the token at the cursor
			const state = token.state; // Get the state from the token
			if (state.inFunction) {
				if (state.functionName !== activeFunctionName) {
					document.getElementById("function-description")?.remove();
					setActiveFunctionName(null);
					if (state.functionName == "if") {
						const FunctionHelperContent = () => {
							const [
								isDocumentationOpen,
								setIsDocumentationOpen
							] = useState(false);
							return (
								<ZCollapse
									panels={[
										{
											key: "0",
											showArrow: false,
											header: (
												<div
													className="function-description-documentation-header"
													onClick={() =>
														setIsDocumentationOpen(
															!isDocumentationOpen
														)
													}
												>
													<ZTypography.ZText
														level={1}
														colorVariant={8}
													>
														if(condition, arg1,
														arg2)
													</ZTypography.ZText>
													<div className="flex-1" />
													<ZIcon
														name={
															isDocumentationOpen
																? EIcons.CHEVRONUP
																: EIcons.CHEVRONDOWN
														}
														size="sm"
													/>
												</div>
											),
											render: <ZFormulaIfDocumentation />
										}
									]}
								/>
							);
						};
						const functionHelper = document.createElement("div");
						functionHelper.setAttribute(
							"id",
							"function-description"
						);
						zFormulaEditorRef.current?.appendChild(functionHelper);
						ReactDOM.render(
							<FunctionHelperContent />,
							functionHelper
						);

						const cursorCoords = editor.cursorCoords(true, "local");

						// Position the dropdown based on cursor coordinates
						functionHelper.style.left = `16px`;
						functionHelper.style.top = `${
							cursorCoords.bottom + 18
						}px`; // `bottom` because you want it just below the cursor
						functionHelper.style.display = "block";
						functionHelper.style.position = "absolute";
						setActiveFunctionName("if");
					}
				}
			} else {
				document.getElementById("function-description")?.remove();
				setActiveFunctionName(null);
			}
		},
		[activeFunctionName]
	);

	// insert function
	const onInsertFunction = useCallback(
		(functionCode: string) => {
			if (functionCode && myEditor) {
				// Check if a non-default option is selected
				const doc = myEditor.getDoc();
				const cursor = myEditor.getCursor(); // Gets the line number in the cursor position
				const pos = {
					// create a new object to avoid mutation of the original selection
					line: cursor.line,
					ch: cursor.ch // set the character position to the end of the line
				};
				doc.replaceRange(functionCode, pos); // Adds the selected value at the current cursor position
				setEditorInsertFunctionsPopover(false);
				setEditorInsertPopover(false);
				myEditor.focus(); // Focus the editor after inserting the value
			}
		},
		[isEditorInitialized]
	);

	// popover states
	const [editorInsertPopover, setEditorInsertPopover] = useState(false);
	const [editorInsertFunctionsPopover, setEditorInsertFunctionsPopover] =
		useState(false);
	const [editorInsertMergeTagsPopover, setEditorInsertMergeTagsPopover] =
		useState(false);

	const [generateFormulaInputValue, setGenerateFormulaInputValue] =
		useState("");
	const [loadingGenerateFormula, setLoadingGenerateFormula] = useState(false);
	const [unableToGenerateFormula, setUnableToGenerateFormula] =
		useState(false);
	const generateFormula = useCallback(async () => {
		if (!myEditor) {
			return;
		}
		try {
			setLoadingGenerateFormula(true);
			const entity = route?.params.entity;
			if (!entity) {
				throw new Error("Entity not found");
			}
			const { result } = await generateFormulaService(
				entity,
				generateFormulaInputValue
			);
			updateEditorValue(result);
		} catch (error) {
			console.error("Error in testing formula", error);
			setUnableToGenerateFormula(true);
		} finally {
			setLoadingGenerateFormula(false);
		}
	}, [generateFormulaInputValue]);

	const [isTryWithAiOpen, setIsTryWithAiOpen] = useState(false);

	const onOpenTryWitAi = () => {
		setIsTryWithAiOpen(true);
		setUnableToGenerateFormula(false);
	};

	const onCancelTryWithAi = () => {
		setIsTryWithAiOpen(false);
		setUnableToGenerateFormula(false);
	};

	return (
		<div ref={zFormulaEditorRef} className="codemirror-container relative">
			<div className="editor-main-area">
				<ZCodeMirrorReactWrapper
					ref={editorRef}
					height="100px"
					options={{
						mode: "zFormulaEditorCustomMode",
						theme: "default",
						lineNumbers: false,
						lineWrapping: true,
						hintOptions: {
							hint: zFormulaEditorCustomHints,
							completeSingle: false
						}
					}}
					onCursorActivity={showFunctionDocumentation}
				/>
			</div>
			{myEditor && (
				<div className="editor-helper flex justify-between">
					{!isTryWithAiOpen && (
						<ZPopover
							visible={editorInsertPopover}
							onVisibleChange={setEditorInsertPopover}
							className="editor-insert-button"
							overlayClassName="editor-insert-popover"
							hideArrow={false}
							trigger="click"
							placement="bottomLeft"
							getPopupContainer={(triggerNode: HTMLElement) =>
								triggerNode
							}
							content={
								<ZMenu>
									<ZMenu.Item key="functions">
										<ZPopover
											visible={
												editorInsertFunctionsPopover
											}
											onVisibleChange={
												setEditorInsertFunctionsPopover
											}
											overlayClassName="editor-insert-functions-popover"
											trigger="click"
											hideArrow
											placement="topLeft"
											content={
												<ZMenu>
													<ZMenu.Item
														onClick={() =>
															onInsertFunction(
																"if(condition, arg1, arg2)"
															)
														}
													>
														if(condition, arg1,
														arg2)
													</ZMenu.Item>
												</ZMenu>
											}
										>
											<div className="flex items-center">
												<span>Functions</span>
												<div className="flex-1 w-2" />
												<ZIcon
													size="sm"
													name={EIcons.CHEVRONRIGHT}
												/>
											</div>
										</ZPopover>
									</ZMenu.Item>
									<ZMenu.Item key="mergeTags">
										<ZPopover
											visible={
												editorInsertMergeTagsPopover
											}
											onVisibleChange={
												setEditorInsertMergeTagsPopover
											}
											overlayClassName="editor-insert-merge-tags-popover"
											trigger="click"
											hideArrow
											placement="right"
											content={
												<ZMergeTagCascadeDropdown
													shouldAllowAddVariable={
														false
													}
													visible={true}
													onSubmit={function (
														key: string
													) {
														const cursor =
															myEditor.getCursor(); // Gets the line number in the cursor position
														const pos = {
															// create a new object to avoid mutation of the original selection
															line: cursor.line,
															ch: cursor.ch // set the character position to the end of the line
														};
														insertMergeTag(
															myEditor,
															pos,
															key
														);
													}}
													onClose={() => {}}
													mergeTagCustomVariables={[]}
													mergeTagSystemVariables={
														filteredMergeTags
													}
												/>
											}
										>
											<div className="flex items-center">
												<span>Merge Tags</span>
												<div className="flex-1 w-2" />
												<ZIcon
													size="sm"
													name={EIcons.CHEVRONRIGHT}
												/>
											</div>
										</ZPopover>
									</ZMenu.Item>
								</ZMenu>
							}
						>
							<ZButton type="link">
								Insert{" "}
								<ZIcon
									name={EIcons.CHEVRONDOWN}
									size="sm"
									className="ml-1 mt-0.5"
								/>
							</ZButton>
						</ZPopover>
					)}
					{formulaFieldsGenerateFormulaFlag && (
						<ZTryWithAiButton
							onOpen={onOpenTryWitAi}
							onCancel={onCancelTryWithAi}
							inputValue={generateFormulaInputValue}
							onInputChange={value => {
								setGenerateFormulaInputValue(value);
								setUnableToGenerateFormula(false);
							}}
							loadingGenerateFormula={loadingGenerateFormula}
							onGenerateFormula={generateFormula}
						/>
					)}
				</div>
			)}
			{myEditor && (
				<div className="flex mt-1">
					{unableToGenerateFormula && (
						<ZTypography.ZText
							className="flex items-center"
							level={0}
							colorVariant={6}
							color={EColors.RED}
						>
							<ZIcon
								name={EIcons.WARNING}
								size="xs"
								color={EColors.RED}
								colorVariant={6}
								className="mr-1"
							/>
							Unable to generate formula
						</ZTypography.ZText>
					)}
					<div className="flex-1" />
					<ZTypography.ZText
						colorVariant={7}
						color={EColors.GRAY}
						className="z-formula-editor-disclaimer"
					>
						AI Doc can make mistakes. Please double check the
						response.
					</ZTypography.ZText>
				</div>
			)}
			{myEditor && (
				<ZFormulaTest
					fieldName={fieldName}
					editor={myEditor}
					mergeTags={filteredMergeTags}
					updateTestedFormula={setTestedFormula}
					updateTestFormulaValidState={updateTestFormulaValidState}
				/>
			)}
		</div>
	);
};

export default memo(ZFormulaEditor);
